import { z } from "zod"

import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"
import {
  platformOperatorIdSchema,
  tenantIdSchema,
} from "./identifiers.js"
import { platformProviderEnvironmentV1Schema } from "./platform-provider-integration-v1.js"

const timestampSchema = z.iso.datetime({ offset: true })
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/)
const versionLabelSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9.+_-]{0,63}$/)
const boundedCountSchema = z.number().int().min(0).max(2_147_483_647)
const boundedSecondsSchema = z.number().int().min(0).max(31_536_000)

export const platformFleetHealthStateV1Schema = z.enum([
  "healthy",
  "degraded",
  "failed",
  "unknown",
])

export const platformFleetComplianceStateV1Schema = z.enum([
  "within_objective",
  "at_risk",
  "breached",
  "unknown",
])

export const platformFleetSeverityV1Schema = z.enum([
  "none",
  "info",
  "warning",
  "critical",
])

const backlogProjectionSchema = z.object({
  state: platformFleetHealthStateV1Schema,
  pendingCount: boundedCountSchema,
  failedCount: boundedCountSchema,
  oldestAgeSeconds: boundedSecondsSchema,
}).strict()

const lagProjectionSchema = z.object({
  state: platformFleetHealthStateV1Schema,
  lagSeconds: boundedSecondsSchema,
  pendingCount: boundedCountSchema,
}).strict()

const backupProjectionSchema = z.object({
  state: platformFleetHealthStateV1Schema,
  lastSuccessfulAt: timestampSchema.nullable(),
  recoveryPointAgeSeconds: boundedSecondsSchema.nullable(),
  rpoState: platformFleetComplianceStateV1Schema,
}).strict()

const certificateProjectionSchema = z.object({
  state: platformFleetHealthStateV1Schema,
  earliestExpiryAt: timestampSchema.nullable(),
  expiringCount: boundedCountSchema,
}).strict()

const providerProjectionSchema = z.object({
  state: platformFleetHealthStateV1Schema,
  degradedCount: boundedCountSchema,
  failedCount: boundedCountSchema,
}).strict()

const capacityProjectionSchema = z.object({
  state: platformFleetHealthStateV1Schema,
  utilizationBasisPoints: z.number().int().min(0).max(10_000).nullable(),
  headroomBasisPoints: z.number().int().min(0).max(10_000).nullable(),
}).strict().superRefine((value, context) => {
  if (
    value.utilizationBasisPoints !== null
    && value.headroomBasisPoints !== null
    && value.utilizationBasisPoints + value.headroomBasisPoints !== 10_000
  ) {
    context.addIssue({
      code: "custom",
      path: ["headroomBasisPoints"],
      message: "Capacity utilization and headroom must total 10,000 basis points.",
    })
  }
})

const sloProjectionSchema = z.object({
  state: platformFleetComplianceStateV1Schema,
  window: z.enum(["five_minutes", "one_hour", "twenty_eight_days"]),
  availabilityBasisPoints: z.number().int().min(0).max(10_000).nullable(),
  errorRateBasisPoints: z.number().int().min(0).max(10_000).nullable(),
  latencyP95Milliseconds: z.number().int().min(0).max(3_600_000).nullable(),
}).strict()

const alertProjectionSchema = z.object({
  highestSeverity: platformFleetSeverityV1Schema,
  openCount: boundedCountSchema,
  owner: codeSchema,
  oldestAgeSeconds: boundedSecondsSchema.nullable(),
}).strict().superRefine((value, context) => {
  if (
    (value.openCount === 0 && value.highestSeverity !== "none")
    || (value.openCount > 0 && value.highestSeverity === "none")
  ) {
    context.addIssue({
      code: "custom",
      path: ["highestSeverity"],
      message: "Alert count and severity must agree.",
    })
  }
})

export const platformFleetHealthObservationV1Schema = z.object({
  contractVersion: z.literal(1),
  projectionId: z.uuid(),
  targetTenantId: tenantIdSchema,
  environment: platformProviderEnvironmentV1Schema,
  site: z.object({
    state: platformFleetHealthStateV1Schema,
    applicationVersion: versionLabelSchema,
    schemaVersion: versionLabelSchema,
  }).strict(),
  queue: backlogProjectionSchema,
  outbox: backlogProjectionSchema,
  reconciliation: lagProjectionSchema,
  backup: backupProjectionSchema,
  certificate: certificateProjectionSchema,
  provider: providerProjectionSchema,
  capacity: capacityProjectionSchema,
  slo: sloProjectionSchema,
  alerts: alertProjectionSchema,
  runbookRef: z.string().regex(/^runbook:\/\/[a-z0-9][a-z0-9/_.-]{2,191}$/),
  observedAt: timestampSchema,
}).strict()

export const platformFleetHealthProjectionV1Schema =
  platformFleetHealthObservationV1Schema.extend({
    version: z.number().int().positive(),
    updatedAt: timestampSchema,
    valueFree: z.literal(true),
  }).strict()

export const recordPlatformFleetHealthObservationV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema,
  observation: platformFleetHealthObservationV1Schema,
}).strict().superRefine((command, context) => {
  const target = command.envelope.target
  if (
    command.envelope.commandType !== "RecordPlatformFleetHealthObservation"
    || command.envelope.commandVersion !== 1
    || command.envelope.idempotencyNamespace !== "fleet.health.observe"
    || target.scope !== "tenant"
    || target.targetTenantId !== command.observation.targetTenantId
    || target.targetType !== "fleet_health_projection"
    || target.targetId !== command.observation.projectionId
  ) {
    context.addIssue({
      code: "custom",
      path: ["envelope"],
      message: "Fleet observation command and exact Tenant target must agree.",
    })
  }
})

export const listPlatformFleetHealthV1Schema = z.object({
  platformOperatorId: platformOperatorIdSchema,
  environment: platformProviderEnvironmentV1Schema.optional(),
  exactTenantId: tenantIdSchema.optional(),
  healthState: platformFleetHealthStateV1Schema.optional(),
  minimumSeverity: z.enum(["info", "warning", "critical"]).optional(),
  actionRequiredOnly: z.boolean().default(false),
  asOf: timestampSchema,
  limit: z.number().int().min(1).max(200).default(50),
  cursor: z.uuid().nullable().default(null),
}).strict()

export const platformFleetHealthSummaryV1Schema = z.object({
  visibleTenantCount: boundedCountSchema,
  healthyCount: boundedCountSchema,
  degradedCount: boundedCountSchema,
  failedCount: boundedCountSchema,
  unknownCount: boundedCountSchema,
  capacityAtRiskCount: boundedCountSchema,
  sloBreachedCount: boundedCountSchema,
  criticalAlertCount: boundedCountSchema,
}).strict().superRefine((value, context) => {
  if (
    value.healthyCount + value.degradedCount + value.failedCount
      + value.unknownCount !== value.visibleTenantCount
  ) {
    context.addIssue({
      code: "custom",
      path: ["visibleTenantCount"],
      message: "Visible Tenant totals must agree.",
    })
  }
})

export const platformFleetHealthPageV1Schema = z.object({
  responseVersion: z.literal(1),
  generatedAt: timestampSchema,
  partial: z.boolean(),
  summary: platformFleetHealthSummaryV1Schema,
  items: z.array(platformFleetHealthProjectionV1Schema).max(200),
  nextCursor: z.uuid().nullable(),
  valueFree: z.literal(true),
}).strict()

export type PlatformFleetHealthStateV1 = z.infer<
  typeof platformFleetHealthStateV1Schema
>
export type PlatformFleetSeverityV1 = z.infer<
  typeof platformFleetSeverityV1Schema
>
export type PlatformFleetHealthObservationV1 = z.infer<
  typeof platformFleetHealthObservationV1Schema
>
export type PlatformFleetHealthProjectionV1 = z.infer<
  typeof platformFleetHealthProjectionV1Schema
>
export type RecordPlatformFleetHealthObservationV1 = z.infer<
  typeof recordPlatformFleetHealthObservationV1Schema
>
export type ListPlatformFleetHealthV1 = z.infer<
  typeof listPlatformFleetHealthV1Schema
>
export type PlatformFleetHealthPageV1 = z.infer<
  typeof platformFleetHealthPageV1Schema
>
